import axios, { AxiosResponse } from 'axios';
import { ApiBaseServiceProvider } from './ApiBaseServiceProvider';

export interface CalendarStatus {
  isConnected: boolean;
  status: 'connected' | 'disconnected' | 'action_required' | 'expired' | 'not_connected';
  accountEmail?: string;
  lastSyncAt?: string;
  errorMessage?: string;
}

export interface CalendarConnectResponse {
  authUrl: string;
}

export interface CalendarEventsResponse {
  events: CalendarEvent[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface CalendarLinkedArticle {
  articleId: string;
  shortId?: string;
  slug?: string;
  isDraft: boolean;
  workspaceShortId?: string;
  workspaceTitle?: string;
  companyId?: string;
}

export interface CalendarEvent {
  _id: string;
  userId: string;
  companyId: string;
  integrationId: string;
  providerEventId: string;
  provider: 'google' | 'outlook' | 'teams';
  title: string;
  description?: string;
  startTime: string;
  endTime: string;
  timeZone: string;
  isAllDay: boolean;
  conferenceUrl?: string;
  conferencePlatform?: 'google_meet' | 'teams' | 'zoom' | 'other';
  organizerEmail: string;
  isOrganizer: boolean;
  attendeeCount?: number;
  isRecurring: boolean;
  recurringEventId?: string;
  botInviteStatus: 'none' | 'inviting' | 'invited' | 'accepted' | 'declined' | 'failed' | 'skipped' | 'recorded';
  botInviteError?: string;
  botInviteRetryCount: number;
  botInvitedAt?: string;
  linkedArticle?: CalendarLinkedArticle | null;
  status: 'confirmed' | 'tentative' | 'cancelled';
  createdAt: string;
  updatedAt: string;
}

export interface CalendarBotInviteResponse {
  success: boolean;
  botInviteStatus: string;
  botInviteError?: string | null;
}

export class CalendarServiceProvider extends ApiBaseServiceProvider {
  scope = 'calendar';

  private url = (path: string) => this.getBaseUrl() + path;

  private get = <T>(path: string) => axios.get<T>(this.url(path), this.getRequestConfig());

  private post = <T, D = undefined>(path: string, data?: D) =>
    axios.post<T, AxiosResponse<T>, D>(this.url(path), data, this.getRequestConfig());

  getStatus = () => this.get<CalendarStatus>('status');

  connect = () => this.post<CalendarConnectResponse>('connect');

  disconnect = () => this.post<{ success: boolean }>('disconnect');

  triggerSync = () => this.post<{ message: string }>('sync');

  getEvents = (params: { startDate: string; endDate: string; page?: number; limit?: number }) => {
    const searchParams = new URLSearchParams();
    searchParams.append('startDate', params.startDate);
    searchParams.append('endDate', params.endDate);
    if (params.page !== undefined) searchParams.append('page', params.page.toString());
    if (params.limit !== undefined) searchParams.append('limit', params.limit.toString());
    return this.get<CalendarEventsResponse>(`events?${searchParams.toString()}`);
  };

  getEvent = (id: string) => this.get<CalendarEvent>(`events/${id}`);

  inviteBot = (eventId: string, options?: { scope?: 'single' | 'all' }) =>
    this.post<CalendarBotInviteResponse, { scope?: 'single' | 'all' }>(`events/${eventId}/invite-bot`, options);

  removeBot = (eventId: string, options?: { scope?: 'single' | 'all' }) =>
    this.post<CalendarBotInviteResponse, { scope?: 'single' | 'all' }>(`events/${eventId}/remove-bot`, options);

  skipEvent = (eventId: string, options?: { scope?: 'single' | 'all' }) =>
    this.post<CalendarBotInviteResponse, { scope?: 'single' | 'all' }>(`events/${eventId}/skip`, options);

  unskipEvent = (eventId: string) => this.post<CalendarBotInviteResponse>(`events/${eventId}/unskip`);
}
